// Mobile layout: the viewer is docked to the top of the page (sticky in CSS) so
// the model stays in view while the controls scroll under it. Scrolling down
// shrinks it; it only returns to full height back at the top of the controls.
// Nothing on desktop is styled off the compact class, so this is inert there.

const COMPACT_CLASS = "viewer-compact";
// Downward scroll needed to shrink, so jitter doesn't trigger it.
const SHRINK_THRESHOLD = 24;
// Scrolling back within this distance of the top is what restores full height.
const TOP_THRESHOLD = 24;
// A flip animates the viewer's height and reflows the page (which can clamp the
// scroll position); ignore scrolling until that settles, otherwise the reflow's
// own scroll events read as scrolling and the viewer oscillates. Must outlast
// the height transition in index.html.
const SETTLE_MS = 300;

// `onViewerRoute` says whether the route that owns the viewer is the one up;
// scrolling any other neither shrinks the viewer nor leaves it shrunk for the
// trip back.
export function wireViewerDock(onViewerRoute: () => boolean) {
  let lastY = clampedScrollY();
  let compact = false;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  let queued = false;
  // Pixels the page loses when the viewer shrinks; invalidated on resize
  // because the docked heights are viewport-relative.
  let shrink: number | null = null;

  // Shrinking costs the page that many pixels of scroll range, so with a short
  // controls panel it clamps the scroll back into the top zone, expands, and
  // flips again. Only shrink while enough range survives to scroll in.
  function shrinkWouldStick(): boolean {
    if (shrink === null) shrink = measureShrink();
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    return scrollable - shrink > TOP_THRESHOLD + SHRINK_THRESHOLD;
  }

  function setCompact(next: boolean) {
    // A flip mid-settle would measure a transitioning page, and cutting the
    // window short would let the reflow's scroll events through.
    if (settleTimer !== null || next === compact) return;
    if (next && !shrinkWouldStick()) return;
    compact = next;
    document.documentElement.classList.toggle(COMPACT_CLASS, next);
    settleTimer = setTimeout(() => {
      settleTimer = null;
      lastY = clampedScrollY();
      update();
    }, SETTLE_MS);
  }

  function update() {
    if (settleTimer !== null) return;
    const y = clampedScrollY();
    if (!onViewerRoute()) {
      setCompact(false);
      lastY = y;
      return;
    }
    if (y <= TOP_THRESHOLD) {
      setCompact(false);
      lastY = y;
      return;
    }
    const delta = y - lastY;
    // Scrolling up short of the top doesn't expand; it just re-anchors, so the
    // next downward run is measured from the highest point reached.
    if (delta < 0) {
      lastY = y;
      return;
    }
    // Leave lastY alone below SHRINK_THRESHOLD so slow scrolling adds up.
    if (delta < SHRINK_THRESHOLD) return;
    setCompact(true);
    lastY = y;
  }

  window.addEventListener(
    "scroll",
    () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        update();
      });
    },
    { passive: true },
  );

  // A viewport change moves the top zone and rescales the docked heights.
  window.addEventListener("resize", () => {
    shrink = null;
    update();
  });
}

// Overscroll (iOS rubber-banding) reports positions outside the document, and
// the bounce back would read as a deliberate direction change.
function clampedScrollY(): number {
  const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  return Math.min(Math.max(0, window.scrollY), max);
}

// Difference between the two docked heights, in pixels. Resolved with an
// off-screen probe rather than by measuring the viewer: its own height is
// animated, so it reads as the pre-transition value.
function measureShrink(): number {
  const probe = document.createElement("div");
  probe.style.cssText = "position:absolute;top:0;left:0;width:0;visibility:hidden";
  document.body.append(probe);
  probe.style.height = "var(--viewer-height)";
  const fullHeight = probe.offsetHeight;
  probe.style.height = "var(--viewer-compact-height)";
  const compactHeight = probe.offsetHeight;
  probe.remove();
  return fullHeight - compactHeight;
}
